import React from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import { Card } from '../../components/ui/card';
import { PageHeader } from '../../components/ui/PageHeader';
import { Rise } from '../../components/ui/motion';
import './AuthShell.css';

/**
 * AuthShell: the one wrapper for Login, Register, Forgot, Reset and Verify.
 *
 * Calm page ground (CC1), a Card at the 28px radius (CC10), the logo link,
 * PageHeader for the display title (CC2) and a SPRING rise-in on mount
 * (CC6). Every page keeps its own copy, state and handlers; the shell only
 * owns chrome. Children are stacked with `.auth-stagger` so pages can wrap
 * their blocks in <Rise index={n}> for the 60ms stagger.
 */
interface AuthShellProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Extra nodes rendered under the subtitle inside the header block. */
  headerExtra?: React.ReactNode;
  /** Optional lucide icon rendered in a 56px teal tile above the title
   *  (confirmation surfaces). `iconTone` switches the tile to amber. */
  icon?: React.ReactNode;
  iconTone?: 'teal' | 'amber';
  /** Centre the header (used by the verifying state). */
  centred?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const AuthShell: React.FC<AuthShellProps> = ({
  title,
  subtitle,
  headerExtra,
  icon,
  iconTone = 'teal',
  centred = false,
  className = '',
  children,
}) => (
  <div className={`auth-page ${className}`.trim()}>
    <Rise as="div" className="auth-card-wrap">
      <Card className={`auth-card${centred ? ' auth-card--centred' : ''}`}>
        <Rise as="div" index={1} className="auth-header">
          <Link to="/" aria-label="Go to homepage" className="auth-logo">
            <BrandLogo width={140} height={34} />
          </Link>
          {icon ? (
            <span className={`auth-icon-tile auth-icon-tile--${iconTone}`} aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <PageHeader as="h1" title={title} description={subtitle} className="auth-page-header">
            {headerExtra}
          </PageHeader>
        </Rise>
        {children}
      </Card>
    </Rise>
  </div>
);

export default AuthShell;
